/** 수령·반납 확인서 (수량 · 부속품 체크리스트 · 사진 · 양쪽 서명) */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { fmtLong, Handover, lineText, Reservation } from './api';

const HandoverRecords: React.FC<{ reservation: Reservation | null; load: (id: string) => Promise<Handover[]>; onClose: () => void }> = ({ reservation, load, onClose }) => {
  const [list, setList] = useState<Handover[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!reservation) return;
    setList(null); setError('');
    load(reservation._id).then(setList).catch((e) => setError(e.message));
  }, [reservation, load]);
  return (
    <Dialog open={!!reservation} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>수령·반납 확인서 · {reservation?.no}</DialogTitle>
      <DialogContent dividers>
        {reservation && (
          <Typography fontSize={13} color="text.secondary" mb={1.5}>
            {reservation.churchName} · {lineText(reservation.lines)} · {fmtLong(reservation.start)} ~ {fmtLong(reservation.end)}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!list && !error && <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}
        {list && !list.length && <Typography color="text.secondary">아직 기록이 없어요.</Typography>}
        {list?.map((h) => (
          <Paper key={h._id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography fontWeight={800} color="success.dark">{h.type === 'pickup' ? '수령' : '반납'} · {h.at}</Typography>
            <Typography fontSize={13} mt={0.5}>
              {h.lines.map((l) => `${l.name} ${l.qty}`).join(', ')}{h.cond ? ` · 상태 ${h.cond}` : ''}{h.note ? ` · 메모: ${h.note}` : ''}
            </Typography>
            {h.checks.length > 0 && (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead><TableRow><TableCell>항목</TableCell><TableCell>기준</TableCell><TableCell>확인</TableCell></TableRow></TableHead>
                <TableBody>
                  {h.checks.map((c, i) => {
                    const bad = c.type === 'ok' ? c.value !== '정상' : c.value !== c.std;
                    return (
                      <TableRow key={i}>
                        <TableCell>{c.item} · {c.label}</TableCell>
                        <TableCell>{c.type === 'ok' ? '정상' : `${c.std}${c.unit || ''}`}</TableCell>
                        <TableCell sx={{ color: bad ? 'error.main' : undefined, fontWeight: bad ? 800 : 400 }}>{c.value}{c.type === 'ok' ? '' : c.unit || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              {h.photos.map((src, i) => (
                <a key={src} href={src} target="_blank" rel="noreferrer"><Box component="img" src={src} alt={`사진 ${i + 1}`} sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} /></a>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1.5, mt: 1.5 }}>
              <Box>
                <Typography fontSize={12} color="text.secondary">{h.type === 'pickup' ? '대여자' : '반납자'} {h.renter} {h.renterPhone}</Typography>
                <Box component="img" src={h.sigRenter} alt="대여자 서명" sx={{ width: '100%', maxHeight: 100, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }} />
              </Box>
              <Box>
                <Typography fontSize={12} color="text.secondary">본부 담당자 {h.staff}</Typography>
                <Box component="img" src={h.sigStaff} alt="담당자 서명" sx={{ width: '100%', maxHeight: 100, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }} />
              </Box>
            </Box>
          </Paper>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button></DialogActions>
    </Dialog>
  );
};

export default HandoverRecords;
